import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Search, Users, MessageCircle, X } from "lucide-react";
import api, { DEFAULT_AVATAR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Structure() {
  const [lgas, setLgas] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  // ward finder
  const [fLga, setFLga] = useState("");
  const [fWard, setFWard] = useState("");
  const [fPu, setFPu] = useState("");

  useEffect(() => {
    api.get("/structure/lgas").then((r) => setLgas(r.data.lgas)).catch(() => {});
  }, []);

  const filtered = lgas.filter((l) => l.name.toLowerCase().includes(q.toLowerCase()));
  const finderLga = lgas.find((l) => l.name === fLga);
  const pus = fWard ? Array.from({ length: 5 }, (_, i) => `${fWard} / PU-${String(i + 1).padStart(3, "0")}`) : [];

  return (
    <div>
      <section className="topo-bg text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <span className="eyebrow text-secondary">Structure & Coordinators</span>
          <h1 className="font-heading font-black text-4xl md:text-5xl mt-3">The Command Structure of Nasarawa</h1>
          <p className="mt-4 text-lg text-emerald-50 max-w-2xl">
            Click any Local Government Area to meet your appointed coordinator, or use the ward finder to locate your local pod.
          </p>
        </div>
      </section>

      {/* WARD FINDER */}
      <section className="max-w-7xl mx-auto px-6 -mt-12 relative z-20">
        <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 p-6 md:p-8">
          <div className="flex items-center gap-2 mb-5">
            <Search className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-bold text-xl">Find My Ward Coordinator</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Select LGA</label>
              <Select value={fLga} onValueChange={(v) => { setFLga(v); setFWard(""); setFPu(""); }}>
                <SelectTrigger data-testid="finder-lga"><SelectValue placeholder="Choose LGA" /></SelectTrigger>
                <SelectContent>{lgas.map((l) => <SelectItem key={l.code} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Select Ward</label>
              <Select value={fWard} onValueChange={(v) => { setFWard(v); setFPu(""); }} disabled={!finderLga}>
                <SelectTrigger data-testid="finder-ward"><SelectValue placeholder="Choose Ward" /></SelectTrigger>
                <SelectContent>{finderLga?.wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 mb-1.5 block">Polling Unit</label>
              <Select value={fPu} onValueChange={setFPu} disabled={!fWard}>
                <SelectTrigger data-testid="finder-pu"><SelectValue placeholder="Choose Unit" /></SelectTrigger>
                <SelectContent>{pus.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {finderLga && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="finder-result"
              className="mt-6 flex flex-col sm:flex-row items-center gap-5 bg-primary/5 border border-primary/15 rounded-2xl p-5">
              <img src={finderLga.coordinator.photo || DEFAULT_AVATAR} alt="" className="w-20 h-20 rounded-2xl object-cover ring-2 ring-primary/20" />
              <div className="flex-1 text-center sm:text-left">
                <div className="text-xs uppercase tracking-wide text-primary font-bold">{finderLga.name} LGA Coordinator</div>
                <div className="font-heading font-extrabold text-xl text-slate-900">{finderLga.coordinator.name}</div>
                <div className="text-sm text-muted-foreground flex items-center justify-center sm:justify-start gap-1 mt-1"><Phone className="w-3.5 h-3.5" /> {finderLga.coordinator.phone}</div>
              </div>
              <a href={finderLga.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="finder-join-pod">
                <Button className="rounded-full bg-[#25D366] hover:bg-[#1eae57] font-bold gap-2"><MessageCircle className="w-4 h-4" /> Join Local Pod</Button>
              </a>
            </motion.div>
          )}
        </div>
      </section>

      {/* LGA DIRECTORY MAP */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <span className="eyebrow">Interactive Directory</span>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl mt-2">The 13 LGAs of Nasarawa</h2>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="lga-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search LGA..." className="pl-9 rounded-full" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((l, i) => (
            <motion.button
              key={l.code}
              data-testid={`lga-tile-${l.code}`}
              onClick={() => setSelected(l)}
              initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
              className="group text-left bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all p-5 overflow-hidden relative"
            >
              <div className="absolute right-3 top-3 font-heading font-black text-4xl text-primary/5 group-hover:text-secondary/30 transition-colors">{l.code}</div>
              <MapPin className="w-6 h-6 text-primary mb-3" />
              <div className="font-heading font-bold text-lg text-slate-900">{l.name}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{l.slogan}</div>
              <div className="mt-3 flex items-center gap-1 text-xs text-primary font-semibold"><Users className="w-3.5 h-3.5" /> {l.wards.length} Wards</div>
            </motion.button>
          ))}
        </div>
      </section>

      {/* LGA DETAIL DIALOG */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          {selected && (
            <div>
              <DialogTitle className="sr-only">{selected.name} LGA Coordinator</DialogTitle>
              <DialogDescription className="sr-only">Coordinator profile, wards and WhatsApp pod for {selected.name} LGA.</DialogDescription>
              <div className="topo-bg p-6 text-white relative">
                <button onClick={() => setSelected(null)} className="absolute right-4 top-4 text-white/70 hover:text-white"><X className="w-5 h-5" /></button>
                <div className="text-xs uppercase tracking-wide text-secondary font-bold">{selected.name} LGA · {selected.slogan}</div>
                <h3 className="font-heading font-black text-2xl mt-1">{selected.name}</h3>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4">
                  <img src={selected.coordinator.photo || DEFAULT_AVATAR} alt="" className="w-20 h-20 rounded-2xl object-cover ring-2 ring-primary/20" />
                  <div>
                    <div className="text-xs uppercase text-primary font-bold">{selected.coordinator.title}</div>
                    <div className="font-heading font-extrabold text-lg">{selected.coordinator.name}</div>
                    <a href={`tel:${selected.coordinator.phone}`} className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><Phone className="w-3.5 h-3.5" /> {selected.coordinator.phone}</a>
                  </div>
                </div>
                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{selected.coordinator.bio}</p>
                <div className="mt-4">
                  <div className="text-xs font-bold uppercase text-slate-500 mb-2">Electoral Wards ({selected.wards.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {selected.wards.map((w) => <span key={w} className="text-xs bg-muted px-2.5 py-1 rounded-full text-slate-700">{w}</span>)}
                  </div>
                </div>
                <a href={selected.whatsapp} target="_blank" rel="noopener noreferrer">
                  <Button className="w-full mt-5 rounded-full bg-[#25D366] hover:bg-[#1eae57] font-bold gap-2" data-testid="dialog-join-pod"><MessageCircle className="w-4 h-4" /> Join {selected.name} WhatsApp Pod</Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
